import type { CartItem } from "@/models/cart"
import type { SelectedCartModifier } from "@/models/cart-item"

const DECIMALS = 2

function safeRound(value: number): number {
  const factor = Math.pow(10, DECIMALS)
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor
}

function toNumber(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Sum of per-unit modifier deltas: Σ (delta_price × row quantity).
 */
export function computeModifiersPerUnit(
  selectedModifiers: SelectedCartModifier[] | undefined
): number {
  if (!Array.isArray(selectedModifiers) || selectedModifiers.length === 0) return 0
  let sum = 0
  for (const m of selectedModifiers) {
    const p = toNumber(m?.delta_price)
    const q = Math.max(0, Math.floor(toNumber(m?.quantity)))
    sum += p * (q > 0 ? q : 1)
  }
  return safeRound(sum)
}

/**
 * Base line total: quantity × (item_price + per-unit modifier extras). Excludes combo child totals.
 */
export function computeLineTotalForCartItemBase(
  itemPrice: number,
  quantity: number,
  options: { selectedModifiers?: SelectedCartModifier[] }
): number {
  const qty = Math.max(0, Math.floor(toNumber(quantity)))
  if (qty <= 0) return 0
  const perUnitExtras = computeModifiersPerUnit(options.selectedModifiers)
  return safeRound(toNumber(itemPrice) * qty + perUnitExtras * qty)
}

/**
 * Calculates cart total: sum of each item's line total (modifiers only).
 */
export function calculateCartTotal(items: CartItem[]): number {
  if (!Array.isArray(items) || items.length === 0) return 0
  const sum = items.reduce((acc, item) => {
    const itemTotal = computeLineTotalForCartItemBase(
      toNumber(item?.basePrice),
      toNumber(item?.quantity),
      { selectedModifiers: item?.selectedModifiers }
    )
    return acc + itemTotal
  }, 0)
  return safeRound(sum)
}
